import React, { useMemo, useState } from 'react';
import { cn } from '@/shared/lib/utils';
import {
  IssueWithChildren,
  IssueType,
  IssueStatus,
  InteractionType,
} from '@momentum/types';
import { GoalInteractionsDialog } from './components/GoalInteractionsDialog';
import { GoalCardContent } from './components/GoalCardContent';
import { RestrictedGoalCard } from './components/RestrictedGoalCard';
import { TasksTable } from '../tasks-table/TasksTable';
import { useGoalStatus } from './hooks/useGoalStatus';
import { ViewGoalDialog } from '@/features/goals/goal-view';
import {
  isRestrictedIssue,
  FullIssueWithChildren,
} from '@/shared/utils/issue-guards';

interface GoalCardProps {
  goal: IssueWithChildren;
  currentEmployeeId: string;
  level: number;
  isExpanded: boolean;
  onToggle: () => void;
  expandedNodes: Set<string>;
  toggleNode: (goalId: string) => void;
  selectedEmployeeId?: string;
  isTarget?: boolean;
  pathGoalIds?: Set<string>;
  targetGoalIds?: Set<string>;
  showCompleted?: boolean;
}

export const GoalCard: React.FC<GoalCardProps> = ({
  goal,
  currentEmployeeId,
  level,
  isExpanded,
  onToggle,
  expandedNodes,
  toggleNode,
  selectedEmployeeId,
  isTarget = false,
  pathGoalIds = new Set(),
  targetGoalIds = new Set(),
  showCompleted = true,
}) => {
  const [showInteractions, setShowInteractions] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [includeInteractionTypes, setIncludeInteractionTypes] = useState<
    InteractionType[]
  >([]);

  const isRestricted = isRestrictedIssue(goal);

  const statusConfig = useGoalStatus(
    isRestricted ? IssueStatus.TODO : (goal.status as IssueStatus)
  );

  const tasks = useMemo(
    () =>
      goal.children?.filter(
        (child: IssueWithChildren) =>
          child.type === IssueType.TASK && !isRestrictedIssue(child)
      ) as FullIssueWithChildren[] | undefined,
    [goal.children]
  );

  const childrenGoals = useMemo(
    () =>
      goal.children
        ?.filter((child: IssueWithChildren) => child.type === IssueType.GOAL)
        .filter(
          (child: IssueWithChildren) =>
            showCompleted ||
            ('status' in child && child.status !== IssueStatus.COMPLETED)
        ),
    [goal.children, showCompleted]
  );

  const hasChildren = useMemo(() => {
    const hasVisibleTasks = Boolean(tasks && tasks.length > 0);

    const visibleChildrenGoals = childrenGoals?.filter(
      (child: IssueWithChildren) => {
        if (!selectedEmployeeId) return true;

        return pathGoalIds.has(child._id);
      }
    );

    const hasVisibleGoalChildren = Boolean(
      visibleChildrenGoals && visibleChildrenGoals.length > 0
    );

    return hasVisibleTasks || hasVisibleGoalChildren;
  }, [tasks, childrenGoals, selectedEmployeeId, pathGoalIds]);

  // Calculate values that depend on goal properties (safe for restricted goals)
  const interactionCount = isRestricted ? 0 : goal.interactionIds?.length || 0;
  const descendantInteractionCount = isRestricted
    ? 0
    : goal.descendantInteractionIds?.length || 0;
  const blockagesCount = isRestricted ? 0 : goal.blockagesCount || 0;

  // Combine direct and descendant interaction IDs for the dialog
  const allInteractionIds = isRestricted
    ? []
    : [
        ...(goal.interactionIds || []),
        ...(goal.descendantInteractionIds || []),
      ];

  if (isRestricted) {
    return (
      <>
        <RestrictedGoalCard goal={goal} level={level} />
        {goal.children && goal.children.length > 0 && (
          <div className="space-y-2 mt-2">
            {tasks && tasks.length > 0 && (
              <TasksTable tasks={tasks} currentEmployeeId={currentEmployeeId} />
            )}
            {childrenGoals &&
              childrenGoals
                .filter((child: IssueWithChildren) => {
                  if (!selectedEmployeeId) return true;

                  return pathGoalIds.has(child._id);
                })
                .map((child: IssueWithChildren) => {
                  const childIsTarget = selectedEmployeeId
                    ? targetGoalIds.has(child._id)
                    : false;

                  return (
                    <GoalCard
                      key={child._id}
                      goal={child}
                      currentEmployeeId={currentEmployeeId}
                      level={level + 1}
                      isExpanded={expandedNodes.has(child._id)}
                      onToggle={() => toggleNode(child._id)}
                      expandedNodes={expandedNodes}
                      toggleNode={toggleNode}
                      selectedEmployeeId={selectedEmployeeId}
                      isTarget={childIsTarget}
                      pathGoalIds={pathGoalIds}
                      targetGoalIds={targetGoalIds}
                    />
                  );
                })}
          </div>
        )}
      </>
    );
  }

  const shouldHaveLowOpacity = selectedEmployeeId && !isTarget;

  return (
    <>
      {!isRestricted && (
        <>
          <GoalInteractionsDialog
            open={showInteractions}
            onOpenChange={setShowInteractions}
            interactionIds={allInteractionIds}
            goalTitle={goal.title}
            includeInteractionTypes={includeInteractionTypes}
          />
          <ViewGoalDialog
            goal={goal}
            open={showViewDialog}
            onOpenChange={setShowViewDialog}
            currentEmployeeId={currentEmployeeId}
          />
        </>
      )}
      <div
        className="w-full"
        style={level > 0 ? { marginInlineStart: `${level * 48}px` } : undefined}
      >
        <div className="w-full transition-all duration-200">
          <div
            className={cn(
              'transition-all duration-200',
              selectedEmployeeId
                ? isTarget
                  ? 'border-s-4 border-s-blue-500'
                  : shouldHaveLowOpacity
                  ? 'opacity-30'
                  : ''
                : ''
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <GoalCardContent
              goal={goal}
              currentEmployeeId={currentEmployeeId}
              hasChildren={hasChildren}
              isExpanded={isExpanded}
              onToggle={onToggle}
              statusConfig={statusConfig}
              interactionCount={interactionCount}
              descendantInteractionCount={descendantInteractionCount}
              blockagesCount={blockagesCount}
              onShowInteractions={(types: InteractionType[]) => {
                setIncludeInteractionTypes(types);
                setShowInteractions(true);
              }}
              onShowView={() => setShowViewDialog(true)}
              isHovered={isHovered}
              level={level}
            />
          </div>

          {goal.children && goal.children.length > 0 && isExpanded && (
            <div
              className="space-y-2 mt-2"
              style={{ marginInlineStart: '48px' }}
            >
              {tasks && tasks.length > 0 && (
                <TasksTable
                  tasks={tasks}
                  currentEmployeeId={currentEmployeeId}
                />
              )}
              {childrenGoals &&
                childrenGoals
                  ?.filter((child: IssueWithChildren) => {
                    // If no employee is selected, show all children
                    if (!selectedEmployeeId) return true;
                    // Only show children that are in a path to employee's goals
                    return pathGoalIds.has(child._id);
                  })
                  .map((child: IssueWithChildren) => {
                    const childIsTarget = selectedEmployeeId
                      ? targetGoalIds.has(child._id)
                      : false;

                    return (
                      <GoalCard
                        key={child._id}
                        goal={child}
                        currentEmployeeId={currentEmployeeId}
                        level={level}
                        isExpanded={expandedNodes.has(child._id)}
                        onToggle={() => toggleNode(child._id)}
                        expandedNodes={expandedNodes}
                        toggleNode={toggleNode}
                        selectedEmployeeId={selectedEmployeeId}
                        isTarget={childIsTarget}
                        pathGoalIds={pathGoalIds}
                        targetGoalIds={targetGoalIds}
                        showCompleted={showCompleted}
                      />
                    );
                  })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
